/**
 * Truck Configuration Service
 * Provides truck axle count configurations for ESAL calculations
 *
 * Data source: ../data/truck-configurations.json (centralized configuration)
 * Excel source: TRAFFIC LOAD.txt truck definitions
 *
 * Used by:
 * - ESALFormulas.ts (ESAL calculation logic)
 *
 * Purpose: Eliminates hardcoded truck configurations and preserves correction history (Camion4: 4→5 axles)
 */

import truckData from '../data/truck-configurations.json';

interface TruckConfiguration {
    truckId: number;
    name: string;
    axleCount: number;
    description?: string;
    correctionNote?: string;
}

/**
 * Truck configuration lookup map
 * Loaded from centralized JSON configuration (../data/truck-configurations.json)
 * Values based on Excel TRAFFIC LOAD.txt definitions
 */
const TRUCK_CONFIGURATIONS: Map<number, TruckConfiguration> = new Map(
    truckData.truckConfigurations.map((config: TruckConfiguration) => [config.truckId, config])
);

/**
 * Default axle count when truck ID not found
 * From Excel: typically 2 axles (light truck)
 */
const DEFAULT_AXLE_COUNT = truckData.defaultAxleCount;

export class TruckConfigurationService {
    /**
     * Get axle count for a truck based on truck ID
     *
     * Matches Excel truck definitions:
     * - Camion1 (ID=1) → 2 axles
     * - Camion2 (ID=2) → 3 axles
     * - Camion3 (ID=3) → 4 axles
     * - Camion4 (ID=4) → 5 axles (CORRECTED from 4)
     * - Camion5 (ID=5) → 5 axles
     * - Camion6 (ID=6) → 6 axles
     * - Camion7 (ID=7) → 7 axles
     * - Camion8 (ID=8) → 8 axles
     *
     * @param truckId - Truck identifier (1-8 for Camion1-Camion8)
     * @returns Number of axles for this truck type
     *
     * @example
     * ```typescript
     * const axles = TruckConfigurationService.getAxleCount(4); // Returns 5 (Camion4)
     * const axlesDefault = TruckConfigurationService.getAxleCount(99); // Returns 2 (default)
     * ```
     */
    static getAxleCount(truckId: number): number {
        const config = TRUCK_CONFIGURATIONS.get(truckId);

        if (config) {
            return config.axleCount;
        }

        // Fallback to default axle count (2 axles)
        return DEFAULT_AXLE_COUNT;
    }

    /**
     * Get complete truck configuration
     * Useful for debugging and UI display
     *
     * @param truckId - Truck identifier
     * @returns Complete truck configuration or undefined
     */
    static getConfiguration(truckId: number): TruckConfiguration | undefined {
        return TRUCK_CONFIGURATIONS.get(truckId);
    }

    /**
     * Get all available truck IDs
     * Useful for validation and UI display
     *
     * @returns Array of available truck IDs (e.g., [1, 2, 3, 4, 5, 6, 7, 8])
     */
    static getAvailableTruckIds(): number[] {
        return Array.from(TRUCK_CONFIGURATIONS.keys()).sort((a, b) => a - b);
    }

    /**
     * Get all truck configurations
     * Useful for debugging and validation
     *
     * @returns Map of truck ID → configuration
     */
    static getAllConfigurations(): Map<number, TruckConfiguration> {
        return new Map(TRUCK_CONFIGURATIONS);
    }

    /**
     * Validate if a truck ID is explicitly defined in the table
     *
     * @param truckId - Truck ID to check
     * @returns true if truck ID is in table, false if will use fallback
     */
    static isTruckDefined(truckId: number): boolean {
        return TRUCK_CONFIGURATIONS.has(truckId);
    }

    /**
     * Get correction note for a truck (if any)
     * Useful for debugging and traceability
     *
     * @param truckId - Truck ID
     * @returns Correction note or undefined if no correction was made
     */
    static getCorrectionNote(truckId: number): string | undefined {
        const config = TRUCK_CONFIGURATIONS.get(truckId);
        return config?.correctionNote;
    }
}
